import React from "react";
import { Grid, Card, CardContent, CardHeader, Button } from "@mui/material";
import { Add, Delete } from "@material-ui/icons";
import { UseFieldArrayReturn } from "react-hook-form";
import { Field, Group } from "../types";
import FormField from "./FormField";
import { createEmptyItem } from "../utils/helpers";

interface FormGroupProps {
  group: Group;
  fields: Field[];
  formValues: any;
  control: any;
  register: any;
  errors: any;
  validationErrors: { [key: string]: string };
  handleBlur: (field: Field) => void;
  fieldArray?: UseFieldArrayReturn;
}

const FormGroup: React.FC<FormGroupProps> = ({ group, fields, formValues, control, register, errors, validationErrors, handleBlur, fieldArray }) => {
  // Skip rendering if the group has a visibility condition that returns false
  if (group.visibility && typeof group.visibility === "function" && !group.visibility(formValues)) {
    return null;
  }

  // Get the group label (which can be a function or a string)
  const groupLabel = typeof group.label === "function" ? group.label(formValues) : group.label;

  // Filter fields that belong to this group and are visible
  const groupFields = fields.filter((field) => field.group === group.name);

  const isMultiGroup = group.isMultiGroup && fieldArray;

  // For regular groups, filter fields by visibility
  const visibleFields = isMultiGroup
    ? groupFields
    : groupFields.filter((field) => !field.visibility || (typeof field.visibility === "function" && field.visibility(formValues)));

  return (
    <Card elevation={0}>
      <CardContent>
        <CardHeader
          title={
            <b
              style={{
                fontSize: "0.9rem",
                margin: "65px 0 10px 0",
                color: "rgb(97, 97, 97)",
                fontFamily: "Title",
              }}
            >
              {groupLabel}
            </b>
          }
        />

        {isMultiGroup ? (
          // Multi-group rendering
          <Grid container spacing={4}>
            {fieldArray.fields.map((item, index) => (
              <React.Fragment key={item.id || index}>
                {groupFields.map((field) => {
                  const isPrimitiveArrayGroup = groupFields[0]?.isMultiField === true;

                  const fieldNamePart = isPrimitiveArrayGroup
                    ? "" // No field name part for primitive arrays
                    : `.${field.name.includes(group.name) ? field.name.split(".").slice(1).join(".") : field.name}`;
                  const finalFieldPath = `${group.name}.${index}${fieldNamePart}`;

                  // Only render the first field if it's a primitive array group
                  if (isPrimitiveArrayGroup && field !== groupFields[0]) {
                    return null;
                  }

                  // Check visibility for multi-group fields
                  if (field.visibility && typeof field.visibility === "function" && !field.visibility(formValues, index)) {
                    return null;
                  }

                  return (
                    <Grid item xs={12} md={4} key={`${index}-${field.name}`}>
                      <FormField
                        field={field}
                        fieldPath={finalFieldPath}
                        control={control}
                        register={register}
                        errors={errors}
                        validationErrors={validationErrors}
                        handleBlur={handleBlur}
                      />
                    </Grid>
                  );
                })}
                <Grid item xs={12}>
                  <Button color="secondary" onClick={() => fieldArray.remove(index)} startIcon={<Delete />} disabled={fieldArray.fields.length === 1}>
                    წაშლა
                  </Button>
                </Grid>
              </React.Fragment>
            ))}
            <Button
              color="primary"
              onClick={() => {
                const isPrimitiveArrayGroup = groupFields[0]?.isMultiField === true;
                const itemToAppend = isPrimitiveArrayGroup ? "" : createEmptyItem(fields, group.name);
                fieldArray.append(itemToAppend);
              }}
              startIcon={<Add />}
              style={{ marginTop: "16px" }}
            >
              დამატება
            </Button>
          </Grid>
        ) : (
          // Regular group rendering
          <Grid container spacing={4}>
            {visibleFields.map((field) => (
              <Grid item xs={12} md={4} key={field.name}>
                <FormField
                  field={field}
                  fieldPath={field.name}
                  control={control}
                  register={register}
                  errors={errors}
                  validationErrors={validationErrors}
                  handleBlur={handleBlur}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

export default FormGroup;
